/**
 * Banner Manager
 * Handle fallback/correction banners for zero-results scenarios
 */

import { elements } from '../core/elements.js';
import { state } from '../core/state.js';
import { SEARCH_COPY } from '../core/constants.js';
import { fillCopy, fillCopyWithSports } from '../decision-engine/copy-templates.js';
import {
  isLocationEffectivelySame,
  hasSpellingMistakes,
} from '../decision-engine/location-comparison.js';
import { normalizeCompareStr } from '../utils/string-utils.js';
import { computeShowDistance } from '../filters/geo-params.js';

/**
 * Apply presentation (header/subtext/labels) to DOM
 * Updates results heading, count, and fallback banner
 * @param {Object} step - Search plan step
 * @param {number} count - Number of results
 */
export function applyPresentation(step, count) {
  state.showDistance = computeShowDistance(step.useDistance, step);

  // Update results heading with label from step
  if (step.labelTemplate && elements.resultsHeading) {
    const label = step.labelTemplate.replace('{count}', count || 0);
    elements.resultsHeading.textContent = label;
  }

  // Update results count
  if (elements.resultsCount) {
    const label = step.labelTemplate
      ? step.labelTemplate.replace('{count}', count || 0)
      : `(${count || 0} camps)`;
    // Extract count part for results-count (format: "(X camps)")
    const countMatch = label.match(/\((\d+)\s+camps\)/);
    if (countMatch) {
      elements.resultsCount.textContent = countMatch[0];
    } else {
      elements.resultsCount.textContent = `(${count || 0} camps)`;
    }
  }

  const hasCopy = step.headerHtml || step.subtextHtml;
  if (elements.fallbackBanner) {
    elements.fallbackBanner.style.display = hasCopy ? 'block' : 'none';
  }
  if (elements.fallbackTitle) {
    elements.fallbackTitle.innerHTML = step.headerHtml || '';
  }
  if (elements.fallbackText) {
    elements.fallbackText.innerHTML = step.subtextHtml || '';
  }
}

/**
 * Hide fallback banner
 */
export function hideFallbackBanner() {
  if (elements.fallbackBanner) elements.fallbackBanner.style.display = 'none';
  if (elements.fallbackTitle) elements.fallbackTitle.innerHTML = '';
  if (elements.fallbackText) elements.fallbackText.innerHTML = '';
}

/**
 * Show filter mismatch banner (when found camp doesn't match selected filters)
 * @param {boolean} show - Whether to show the banner
 */
export function showFilterMismatchBanner(show) {
  if (show) {
    if (elements.fallbackBanner) {
      elements.fallbackBanner.style.display = 'block';
    }
    if (elements.fallbackTitle) {
      elements.fallbackTitle.innerHTML = "Selected filters don't match this camp.";
    }
    if (elements.fallbackText) {
      elements.fallbackText.innerHTML = 'Explore nearby camps that match your search.';
    }
  } else {
    hideFallbackBanner();
  }
}

/**
 * Show corrected location banner if needed
 * Handles spelling mistakes and location mismatches
 * @param {number} count - Number of results (default: 0)
 */
export function showCorrectedLocationBannerIfNeeded(count = 0) {
  if (!state.showCorrectedLocationBanner) return;
  if (!state.geocodeResult?.recognized) return;

  // Don't show "couldn't find camps" message if camps were actually found
  if (count > 0) {
    hideFallbackBanner();
    return;
  }

  const rawInput = state.geocodeResult.rawInput || state.zip || '';
  const correctedLabel = state.geocodeResult.correctedLocationLabel || '';
  const correctedCity = state.geocodeResult.correctedCity || '';

  // Check if city name matches
  const cityMatches =
    correctedCity && rawInput
      ? isLocationEffectivelySame(rawInput, correctedCity) ||
        normalizeCompareStr(rawInput).includes(
          normalizeCompareStr(correctedCity)
        ) ||
        normalizeCompareStr(correctedCity).includes(
          normalizeCompareStr(rawInput)
        )
      : false;

  // Only check for typos if city doesn't match
  const hasTypos =
    !cityMatches &&
    hasSpellingMistakes(rawInput, correctedLabel, correctedCity);

  // Use "unrecognized" message for spelling mistakes, "normalizedMismatch" for other mismatches
  const headerHtml =
    hasTypos && !cityMatches
      ? fillCopy(SEARCH_COPY.unrecognizedHeader, { rawInput })
      : fillCopy(SEARCH_COPY.normalizedMismatchHeader, { rawInput });
  const subtextHtml = hasTypos
    ? fillCopyWithSports(SEARCH_COPY.unrecognizedSubtextUser, {
        locationLabel: correctedLabel,
      })
    : fillCopyWithSports(SEARCH_COPY.normalizedMismatchSubtext, {
        locationLabel: correctedLabel,
      });

  applyPresentation(
    {
      useDistance: true,
      headerHtml,
      subtextHtml,
    },
    count
  );
}

console.log('📦 Banner manager module loaded');
